import React, {ChangeEvent, createRef, ReactElement, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import './TransactionCard.less';
import '@/stylesheets/form.less';
import Transaction from "@/entities/transaction";
import util from "@/lib/util";
import * as DateUtil from "@/lib/dateutils";
import OkCancel from "@/ui/Buttons/OkCancel";
import FormElement from "@/lib/FormElement";
import Matter from "@/entities/matter";
import CaseHelper from "@/Case/CaseHelper";
import IconButton from "@/ui/IconButton/IconButton";
import Contact from "@/entities/contact";
import FinancialHelper from "../FinancialHelper";
import Lead from "@/entities/lead";
import ContactSelect, {ContactSelectHandle} from '@/Contacts/ContactSelect/ContactSelect';
import failureLadder from '@/lib/failureLadder';
import paymentApi from '../PaymentProcessor/paymentApi';
import PaymentActionBox, {PaymentActionBoxHandle} from '../PaymentProcessor/forms/PaymentActionBox';
import * as TransactionActions from '@/store/actions/transactionActions';
import LeadHelper from '@/CRM/Sales/LeadHelper';
import Alert from '@/lib/alert';
import i18n from '@/i18n/i18n';
import HelpMessage from '@/i18n/HelpMessage';
import Auth from '@/Auth/Auth';
import {formatFML, formatFMLFromId, formatLFM, sortContacts} from "@/Contacts/ContactFormat";
import find from "@/store/find";
import {getCurrentUserId, isPortalUser} from "@/lib/picos/currentLogin";
import Icons from "@/ui/IconButton/Icons";
import newHRTimestamp from "@/lib/picos/timestamp";
import upFirstLetter from "@/lib/picos/upFirstLetter";
import type {RootState} from "@/@types/store";
import deepEqual from 'fast-deep-equal/es6';
import {uuid} from "@/@types/entity";
import {TransactionChildCardProps} from "./TransactionCard";
import type {PaymentMethod, ProcessorPaymentMeta, StandardAccount} from "@/@types/payments";
import type {PayrixPaymentMeta} from "@/@types/payrix";
import type {SelectOption} from "@/@types/select";
import useIsMounted from "@/hooks/useIsMounted";
import {useRootDispatch, useRootSelector} from "@/hooks/useOutlawStore";
import useStateWithCallback from "@/hooks/useStateWithCallback";
import useComponentCache from "@/hooks/useComponentCache";
import usePrevious from "@/hooks/usePrevious";
import {CheckboxClickEvent} from "@/ui/Checkbox";
import {DateWithCheckbox} from "@/ui/forms/DateWithCheckbox";
import Payrix from "@/Financial/PaymentProcessor/Payrix/Payrix";
import {chargeToStore} from "@/Financial/PaymentProcessor/Payrix/refundCharge";
import {isLawpayFormat, normalizePaymentMeta} from "@/Financial/PaymentProcessor/normalizePaymentMeta";
import {PaymentMethodIcon} from "./paymentMethodIcon";
import {getPayerFetchContactIds} from "./payerFetch";

/**
 * List of available payment methods.  Note that Trust Account will require additional information to be displayed
 * (such as which trust account).
 * @type {string[]}
 */
const PAYMENT_METHODS: string[] = [
    'Cash', 'Check', "Cashier's Check", 'Money Order',
    'Credit Card', 'Debit Card',
    'ACH', 'E-Check', 'Wire Transfer',
    'Apple Pay', 'Google Pay',
    'Zelle', 'Venmo', 'CashApp', 'PayPal',
    'Other'
];

const PAYMENT_PROCESSOR_METHODS: string[] = [
    'Cash', 'Check', "Cashier's Check", 'Money Order',
    'Credit Card', 'Debit Card',
    'ACH', 'E-Check', 'Wire Transfer',
    'Apple Pay', 'Google Pay',
    'Zelle', 'Venmo', 'CashApp', 'PayPal',
    'Other'
];

type TrustTypes = 'G' | 'M';

type TrustOptionData = {
    id: string,
    type: string,
    name: string | ReactElement,
    balance: number
}

export type DistributionTarget = {
    label: string,
    value: string
}

type PaymentCardState = {
    bankAccounts: StandardAccount[],
    isFetching: boolean,
    isMatterSet: boolean,
    hasPaymentBeenTaken: boolean,
    // Note: 'current' and 'paymentMethods' are intentionally not cached
    // - 'current' should always come from props to avoid stale transaction state
    // - 'paymentMethods' are contact-specific and should be fetched fresh
}

/**
 * Derive the date to stamp on a voided/refunded transaction from the processor
 * meta. Prefer the processor's actual void/refund timestamp
 * (`meta.voidOrRefundDate`, the Payrix `modified` time) — essential for a
 * void/refund discovered on a later refresh that actually happened in the past
 * — and fall back to "now" only when the meta carries no date (a brand-new live
 * action). Single source of truth so the three writers that stamp voidedDate
 * (onPaymentVoided / onPaymentRefunded / onPaymentActionChange) can never drift
 * apart. (OWA-187 / OWA-570)
 */
export const voidedDateFromMeta = (meta?: PayrixPaymentMeta | null): Date =>
    meta?.voidOrRefundDate ?? new Date();

/**
 * See documentation for the Transaction schema (schemas/transaction.js) for a full rundown on how the Transaction works,
 * and which bits are used for each type of transaction.
 *
 * This card can be called from either an EditableTable or directly from a button press.  The env and startEditing props
 * are defaulted so that coming in from outside of an EditableTable still works.
 *
 * There are two ways that this card is shown: from an EditableTable of transactions in Financials, and directly from a
 * Matter or Contact.
 *
 * Check matter and client to see if either of these cases are true.
 *
 * If we are called from a Matter, we will assume that the matter and client are unchangeable.
 * If we are called from a Contact, we will assume that the contact is unchangeable, but the matter is.
 *
 * For a payment, we show a contact first, then list their cases. Click on the cases that we will apply the funds to
 */
function PaymentCard(props: PaymentCardProps) {
    const isMounted = useIsMounted();
    const {client, lead, matter, onCancel, onChange, onOk, readOnly} = props;

    // OWA-552: resolve a non-null `current` in code, not via a prop-type contract — the
    // guarantee lives in this card, so it holds even if PaymentCard is ever rendered
    // without a current. Normally props.current is present (the parent passes its resolved
    // current), so this is the SAME reference — important for the no-lag behaviour below.
    const propsCurrent = props.current;
    if (!propsCurrent && import.meta.env.DEV) console.warn('<PaymentCard/> rendered without a current Transaction; using a blank fallback.');
    // useMemo so `current` is a stable reference across renders (it feeds many hook deps);
    // when props.current is present (always, in practice) this is that same reference.
    const current = useMemo(() => propsCurrent ?? new Transaction({}), [propsCurrent]);

    const {cacheSave, cacheReset, cacheValue} = useComponentCache<PaymentCardState>(`payment-card-${current.id || props.newId}`);

    const dispatch = useRootDispatch();

    // `current` (resolved above) aliases the parent TransactionCard's live current — the
    // SAME reference whenever props.current is present (i.e. always in practice). The amount
    // field is rendered through the shared FormElement built from the parent's current. A
    // separate local copy here (previously useState synced via a deferred setTimeout) lagged
    // behind, so a child-originated change (notes/payer/method) would clone the stale copy and
    // clobber the amount the user just typed — which is why we alias, never copy. (OWA-490/OWA-552)
    const [currentBankAccounts, setCurrentBankAccounts] = useState<StandardAccount[]>(cacheValue('bankAccounts', []));
    // Don't cache 'paymentMethods' - they're contact-specific and should be fetched fresh
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
    const [isFetching, setIsFetching] = useState<boolean>(cacheValue('isFetching', false));
    const [isMatterSet, setIsMatterSet] = useState<boolean>(cacheValue('isMatterSet', false));
    // Only restore hasPaymentBeenTaken from cache if the transaction hasn't already been
    // processed. A reopened transaction with an existing processorPaymentMeta isn't mid-flow,
    // so a stale cached `true` must NOT collapse the UI into the "OK only, no Cancel" state.
    const [hasPaymentBeenTaken, setHasPaymentBeenTaken] = useStateWithCallback<boolean>(
        current.processorPaymentMeta ? false : cacheValue('hasPaymentBeenTaken', false)
    );

    const isFetchingAccounts = useRef<boolean>(false);
    const isFetchingPaymentMethods = useRef<boolean>(false);

    const wasFetching = usePrevious<boolean>(isFetching);

    const bankAccounts = useRootSelector(state => state.bankAccounts);
    const contacts = useRootSelector(state => state.contacts);
    const leads = useRootSelector(state => state.leads);
    const matters = useRootSelector(state => state.matters);
    // Benefactor detection lives entirely in the participants slice (CaseHelper.getBenefactors).
    // Subscribe to it so the payer list coloring and the payment-method fetch recompute when a
    // benefactor's participant record syncs in after mount (another tab, late sync, just-added). (OWA-485)
    const participants = useRootSelector(state => state.participants);
    const misc = useRootSelector(state => state.misc);

    const doResetRef = useRef<boolean>(false);
    const isProcessingRef = useRef<boolean>(false);
    const payerRef = createRef<ContactSelectHandle>();
    const paymentActionBoxRef = useRef<PaymentActionBoxHandle>(null);

    /**
     * Get all contacts in the system, used for source type of 'C'.  Put the clients for this transaction at the top,
     * along with Benefactors.  This is only used to populate sources, not targets.
     * of the list.
     *
     * @param {Transaction} [tr] - transaction, if there is one
     */
    const getContacts = useCallback((tr?: Transaction) => {
        // Get the clients related to this client/matter
        const clients = current.clientIds || [];

        // If this is payment on a lead, the matterId will be a leadId
        const _matters = current.matterIds.length === 0
                         ? lead // No matterIds, what about a lead?
                           ? [lead] // Return the passed in lead
                           : CaseHelper.getMattersForClients(current.clientIds, true) // Just get the matters for the clientIds
                         : current.entityType === 'lead' // There are matterIds, but is it a leadId?
                           ? [leads.get(current.entityId)]
                           : current.matterIds.map(mid => CaseHelper.getMatterFromId(mid) || matter);

        // We allow leads to fund a retainer
        const leadPayers = (current.txType === 'FR' || current.txType === 'PC')
                           ? lead
                             ? lead.payers
                             : util.flatten(leads
                    .filter(l => util.arrayIntersection(l.clients, clients).length > 0)
                    .map(l => l.payers))
                           : [];

        // Find clients for matters
        const matterClients = lead ? lead.clients : util.flatten(_matters.filter(util.existsFilter).map(m => m.clients));

        // Get benefactors for the cases
        let benefactors: string[] = [];
        if (lead) benefactors = util.arrayMerge(lead.payers, LeadHelper.getBenefactors(lead, participants));
        else benefactors = CaseHelper.getBenefactors(_matters.filter(util.existsFilter), participants);

        // PC and FR can be paid by anyone — clients, benefactors, businesses (insurers,
        // courts, opposing counsel, expert refunds), or any other contact. The dropdown
        // lists everyone, but groups + labels them so clients and benefactors stay
        // prominent at the top. Other txTypes keep the original restriction to the
        // matter's people.
        const allowAnyContact = current.txType === 'FR' || current.txType === 'PC';

        const benefactorIdSet = new Set<string>([...benefactors, ...leadPayers]);
        const clientIdSet = new Set<string>(matterClients);

        let ordered: Contact[];
        if (allowAnyContact) {
            const byName = (a: Contact, b: Contact) => formatLFM(a).localeCompare(formatLFM(b));
            // Partition into the four tiers, removing each tier's members from
            // subsequent tiers so a benefactor/client/business doesn't appear twice.
            const tier1 = contacts.filter(c => benefactorIdSet.has(c.id))
                .sort(byName);
            const tier2 = contacts.filter(c => clientIdSet.has(c.id) && !benefactorIdSet.has(c.id))
                .sort(byName);
            const tier3 = contacts.filter(c => c.isCompany && !benefactorIdSet.has(c.id) && !clientIdSet.has(c.id))
                .sort(byName);
            const tier4 = contacts.filter(c => !c.isCompany && !benefactorIdSet.has(c.id) && !clientIdSet.has(c.id))
                .sort(byName);
            ordered = [...tier1, ...tier2, ...tier3, ...tier4];
        }
        else {
            // Original restricted behavior for non-FR/PC tx types.
            const targetIds = clients.concat(benefactors)
                .concat(leadPayers)
                .concat(matterClients)
                .filter(util.distinctFilter);
            ordered = contacts
                .filter(c => targetIds.includes(c.id))
                .sort((a, b) => targetIds.includes(a.id)
                                ? -1
                                : targetIds.includes(b.id)
                                  ? 1
                                  : formatLFM(a).localeCompare(formatLFM(b)));
        }

        const results = ordered
            .map(c => {
                const name = formatLFM(c);
                const label = benefactorIdSet.has(c.id)
                              ? FinancialHelper.formatLabel(Icons.MONEY_IN, name, null, 'green')
                              : clientIdSet.has(c.id)
                                ? FinancialHelper.formatLabel(Icons.CLIENT, name, null, 'navy')
                                : allowAnyContact && c.isCompany
                                  ? FinancialHelper.formatLabel(Icons.BUSINESS, name, null, 'gray')
                                  : allowAnyContact
                                    ? FinancialHelper.formatLabel(Icons.CONTACT, name, null, 'gray')
                                    : null;

                if (label) return {
                    label,
                    contactName: name,
                    value: `${c.id}`,
                    contact: c
                }
                else return null;
            })
            .filter(util.existsFilter);

        if (tr) return results.filter(v => v.value === tr.source);
        else return results;
    }, [contacts, current, lead, leads, matter, participants]);

    const contactOptions = useMemo(() => getContacts(), [getContacts]);

    // useLayoutEffect(() => {
    //     if (wasFetching && !isFetching && !current.source) {
    //         if (contactOptions?.length > 0 && !current.source) {
    //             const clone = current.clone();
    //             clone.source = contactOptions[0].contact.id;
    //             // eslint-disable-next-line react-hooks/set-state-in-effect
    //             setCurrent(clone);
    //         }
    //     }
    // }, [isFetching, wasFetching, contactOptions, current]);

    /**
     * Debounced method for getting payment info
     * @param {Contact[]} contacts
     * @private
     */
    const updatePaymentInfo = useCallback(async (contacts: Contact[]) => {
        // Fetch payment methods
        if (!isFetchingPaymentMethods.current) {
            if (paymentMethods.length === 0) {
                isFetchingPaymentMethods.current = true;
                setIsFetching(true);
            }

            paymentApi.getPaymentMethodsMulti(contacts)
                .then(_paymentMethods => {
                    if (isMounted() && !deepEqual(_paymentMethods, paymentMethods)) {
                        setPaymentMethods(_paymentMethods);
                    }
                })
                .catch(err => {
                    Alert.add(new Alert(
                        Alert.WARNING,
                        util.genUUID(),
                        0,
                        i18n.ls('transaction', 'errFetchPayment', {
                            name: paymentApi.getName(),
                            err: err.message
                        })));
                })
                .finally(() => {
                    isFetchingPaymentMethods.current = false;
                    if (!isFetchingAccounts.current) setIsFetching(false);
                });
        }

        // Fetch accounts
        if (!isFetchingAccounts.current) {
            if (bankAccounts.length === 0) {
                isFetchingAccounts.current = true;
                setIsFetching(true);
            }

            paymentApi.getAccounts()
                .then(_bankAccounts => {
                    // OWA-593 Bug C: getAccounts resolves null when disconnected (and
                    // undefined on error). Guard before mapping so a null result doesn't
                    // throw into the .catch and raise a spurious "error fetching" warning
                    // (mirrors the result!==null checks in the other getAccounts callers).
                    if (_bankAccounts && isMounted() && !deepEqual(_bankAccounts.map(ba => ba.theirId), currentBankAccounts.map(ba => ba.theirId))) {
                        setCurrentBankAccounts(_bankAccounts)
                    }
                })
                .catch(err => {
                    Alert.add(new Alert(
                        Alert.WARNING,
                        util.genUUID(),
                        0,
                        i18n.ls('transaction', 'errFetchPayment', {
                            name: paymentApi.getName(),
                            err: err.message
                        })));
                })
                .finally(() => {
                    isFetchingAccounts.current = false;
                    if (!isFetchingPaymentMethods.current) setIsFetching(false);
                });
        }
    }, [bankAccounts, currentBankAccounts, isMounted, paymentMethods]);

    const updateProcessorInfo = useCallback(() => {
        // Check for new payment methods
        if (paymentApi.isConnected() && misc.isConnected) {
            // Resolve the set of contacts whose stored payment methods we fetch: the case's
            // people AND the contact actually selected as payer. See getPayerFetchContactIds
            // (OWA-485). `participants` is the subscribed slice so benefactor detection stays
            // reactive to records that sync in after mount.
            const contactIds = getPayerFetchContactIds(client, lead, matter, {
                clientIds: current.clientIds,
                entityId: current.entityId,
                entityType: current.entityType,
                matterIds: current.matterIds,
                source: current.source,
                sourceType: current.sourceType,
            }, participants);

            const usingLawpay = paymentApi.isLawpay()
            // find() is typed Contact | null, but these ids come from the case's resolved
            // people, so a contact is expected; `!` keeps the existing non-null assumption
            // (the filter already dereferences c.processorContactId and the results flow
            // straight through unchanged). (OWA-554)
            const filteredContacts = contactIds.filter(util.distinctFilter)
                .map(cid => find<Contact>('contacts', cid)!)
                .filter(c => usingLawpay ? c.processorContactId : true);

            updatePaymentInfo(filteredContacts)
                .catch(err => Alert.addSimpleError(err.message));
        }
    }, [client, current.clientIds, current.entityId, current.entityType, current.matterIds, current.source, current.sourceType, lead, matter, misc.isConnected, participants, updatePaymentInfo]);

    useEffect(() => {
        if (paymentApi.usesPayments() && paymentApi.isConnected() && current.processorPaymentMeta?.id && isProcessingRef.current === false) {
            // LawPay transactions don't exist in Payrix — skip the API call entirely.
            // The metadata we have in the database is the final state for these transactions.
            // However, ensure voidedDate is set for refunded/voided transactions
            // (mirrors what fetchTxn does for Payrix at PaymentActionBox lines 150-155).
            if (isLawpayFormat(current.processorPaymentMeta)) {
                const meta = normalizePaymentMeta(current.processorPaymentMeta);
                if (meta?.voidOrRefundDate && !current.voidedDate) {
                    // Guard against re-entry: the outer if checks isProcessingRef === false,
                    // so setting it true prevents duplicate dispatches while the parent's
                    // saved transaction flows back down through props.current.
                    isProcessingRef.current = true;
                    const clone = current.clone();
                    clone.voidedDate = meta.voidOrRefundDate;
                    clone.addedOn = newHRTimestamp();
                    clone.addedBy = getCurrentUserId();
                    dispatch(TransactionActions.saveTransactionQueue(clone));
                    onChange(clone, true);
                }
                return;
            }

            isProcessingRef.current = true;

            const meta = current.processorPaymentMeta;
            // The enclosing effect only runs when `current.processorPaymentMeta?.id` is
            // truthy, so id is a real string here. (OWA-554)
            const txnId = meta.id!;

            paymentApi.getTransaction(txnId)
                .then((answer) => {
                    // OWA-978: after a refund the answer is the refund, but the payment stores its
                    // charge. With no charge to store, the stored metadata stays as it is.
                    const txn = chargeToStore(answer);
                    if (!txn) return;
                    const newMeta = Payrix.parsePaymentMeta(txn, current);

                    // If txn.description is null, this is a really old transaction where we need to update the metadata.
                    if (txn.description === null || txn.description.trim().length === 0) {
                        // We need to update the transaction metadata, specifically the description field.
                        paymentApi.updateTxn(txn.id, JSON.stringify(newMeta))
                            .catch(console.error);
                    }

                    // Check the dates and txnIds to see if they match what is in meta. If not, this is the new meta (make
                    // sure to parse it first, of course).
                    if (!deepEqual(newMeta, meta)) {
                        const clone = current.clone();
                        clone.addedOn = newHRTimestamp();
                        clone.addedBy = getCurrentUserId();
                        clone.processorPaymentMeta = newMeta;

                        dispatch(TransactionActions.saveTransactionQueue(clone));
                        onChange(clone, true);
                    }
                })
                .catch(err => {
                    if (err.status !== 404) console.error(err);
                })
                .finally(() => {
                    isProcessingRef.current = false;
                })
        }
    }, [currentBankAccounts, cacheReset, dispatch, hasPaymentBeenTaken, isMatterSet, paymentMethods, current, onChange]);

    useEffect(() => {
        setTimeout(updateProcessorInfo, 0);
    }, [updateProcessorInfo]);

    useEffect(() => {
        const doReset = doResetRef.current;
        const handle = paymentActionBoxRef.current;

        return () => {
            if (doReset) {
                cacheReset();
                // The PaymentActionBox is only mounted for processor payment methods
                // (usingPM && amount > 0 && accountId). For Cash/Check/etc. the ref is
                // null, so guard before notifying it of the parent close. (OWA-490)
                handle?.onParentClose();
            }
            // Don't cache 'current' or 'paymentMethods' - they cause stale state issues
            else cacheSave({bankAccounts: currentBankAccounts, hasPaymentBeenTaken, isFetching, isMatterSet});
        }
    }, [cacheReset, cacheSave, currentBankAccounts, hasPaymentBeenTaken, isFetching, isMatterSet]);

    /**
     * Handle paymentDistribution field changes.  The structure of the paymentDistribution field
     * is an array of {matter: uuid, amount: float, notes: string} entries.
     *
     * @param {ChangeEvent<HTMLInputElement>} e
     * @param {Matter} matter - the related matter
     * @private
     */
    const handlePaymentDistribution = useCallback((e: ChangeEvent<HTMLInputElement>, matter: Matter) => {
        e.preventDefault();

        const amount = e.target.value;
        const transaction = current.clone();
        const paymentDistribution = current.paymentDistribution ? [...current.paymentDistribution] : [];

        // Find (or create) an entry for the field
        let index = paymentDistribution.findIndex(pdi => pdi.matterId === matter.id);
        if (index < 0) paymentDistribution.push({matterId: matter.id, amount, notes: ''});
        else {
            paymentDistribution[index].amount = amount;
        }

        transaction.paymentDistribution = paymentDistribution;

        onChange(transaction);
    }, [current, onChange]);

    const allMatters = useMemo(() => {
        if (matter && !matters.find(m => m.id === matter.id))
            return [...matters.values(), matter];
        else return matters;
    }, [matter, matters]);

    /**
     * Get trust accounts for the clients and matters.
     * There will be one trust per matter, and one trust for each client in common to all matters.
     *
     * However, if this is a payment, and there are multiple matters selected, the money could be divided into
     * portions for client trust(s) and matter trust(s).  Any division is ok.
     *
     * Trusts are used as a target (payment is always an external source).  Note that the targetType for client
     * trust is 'G', not 'C'!  A target of C refers to a distribution to a 3rd party, which does not happen from
     * a payment type.
     *
     * Need to look at the 'field' parameter (to/from) to make sure we are not allowing the same to/from funding source.
     * We do this by making sure the source and target id's are not the same.
     *
     * @param {TrustTypes[]} types - 'G' or 'M' for client trust or matter trust
     * @param {'to'|'from'} [field]
     * @return {Array.<{label: string, value: uuid, data: object}>}
     */
    const getTrustAccounts = useCallback((types: TrustTypes[], field: 'to' | 'from' = 'from'): SelectOption<string, TrustOptionData>[] => {
        // Otherwise get matters specific to the case
        // getMatterFromId is Matter | undefined; `!` preserves the current behaviour of
        // throwing on an unresolvable matterId rather than silently dropping a matter from
        // the trust totals (PCI: trust math must stay exact). (OWA-554)
        const _matters = matter
                         ? [matter]
                         : current.matterIds.length > 0
                           ? current.matterIds.map(mid => CaseHelper.getMatterFromId(mid)!)
                           : matters.filter(m => util.arrayIncludesAllTerms(current.clientIds, m.clients));

        // Get an array of clientId arrays [[],[],...]
        // let clientIds = matters.map(m => m.clients);

        // If more than one result, get a list of Ids that are in common to each of the matters
        // if (matters.length > 1) clientIds = clientIds.reduce((a, b) => a.filter(c => b.includes(c)), []);
        // else clientIds = clientIds.flat();

        const _readOnly = readOnly || current.invoiceId;

        // If not clients in common, we can not pull from any client trusts.
        const clientTrusts: TrustOptionData[] = types.includes('G')
                                                ? current.clientIds
                                                    .map(cid => {
                                                        // Get the balance for this trust
                                                        const contact = find<Contact>('contacts', cid);
                                                        const clientName = formatLFM(contact, true);
                                                        const trustBalance = FinancialHelper.getTrustAmount('G', cid);

                                                        return {
                                                            id: cid,
                                                            type: 'G',
                                                            name: FinancialHelper.formatLabel(
                                                                Icons.USER,
                                                                i18n.ls('transaction', 'trustFor', {name: clientName}),
                                                                trustBalance,
                                                                'blue'
                                                            ),
                                                            balance: trustBalance
                                                        };
                                                    })
                                                : [];

        // Add the trust for the matter
        const matterTrusts: TrustOptionData[] = types.includes('M') ? _matters
            .map(matter => {
                const trustAmount = FinancialHelper.getTrustAmount('M', matter.id);

                return {
                    id: matter.id,
                    type: 'M',
                    name: FinancialHelper.formatLabel(
                        Icons.CASE,
                        i18n.ls('transaction', 'trustFor', {name: matter.name}),
                        trustAmount,
                        'brown'
                    ),
                    balance: trustAmount
                };
            }) : [];

        const merged: TrustOptionData[] = matter ? [...matterTrusts, ...clientTrusts] : [...clientTrusts, ...matterTrusts]
            .filter(obj => {
                // source has to have at least as much funds as the amount (if not read-only)
                return (field === 'from' && !_readOnly)
                       ? obj.balance >= (current.amount || 0)
                       : true;
            });

        return merged.map(account => {
            return {
                label: account.name,
                value: account.id,
                data: account
            };
        });
    }, [current.amount, current.clientIds, current.invoiceId, current.matterIds, matter, matters, readOnly]);

    // /**
    //  * Get bank accounts in Select format.  If a type is given ([T]rust/[O]perating), limit to that type.
    //  *
    //  * @param {AccountTypeType[]} [types]
    //  */
    // getBankAccounts = (types: AccountTypeType[]): {
    //     label: ReactElement,
    //     value: string,
    //     data: {
    //         id: string,
    //         type: string,
    //         name: ReactElement,
    //         // balance: FinancialHelper.getTrustAmount(ba) // TODO: This is almost definitely wrong
    //     }
    // }[] => {
    //     // Filter to see which bank accounts to show
    //     const filter = (ba: BankAccount) => {
    //         // Only include accounts of the given types
    //         return !(types && !types.includes(ba.accountType));
    //     };
    //
    //     return bankAccounts
    //         .filter(filter)
    //         .map(ba => {
    //             const icon = ba.accountType === 'T' ? Icons.SCALES : ba.accountType === 'O' ? Icons.BANK : Icons.ESCROW;
    //             return {
    //                 label: FinancialHelper.formatLabel(icon, ba.name, ba.balance),
    //                 value: ba.id,
    //                 data: {
    //                     id: ba.id,
    //                     type: ba.accountType, // 'B'
    //                     name: FinancialHelper.formatLabel(icon, ba.name, ba.balance),
    //                     // balance: FinancialHelper.getTrustAmount(ba) // TODO: This is almost definitely wrong
    //                 }
    //             };
    //         });
    // };

    /**
     * Payments are always made to a trust (client or matter), an operating account, or an Escrow account.
     *
     * List trust accounts for each client and matter, along with the Operating and Escrow accounts.
     *
     * If there are multiple matters selected, we will need to show only the client trust, no matter trusts
     *
     * @param {'to'|'from'} [field]
     */
    const getPaymentAccounts = useCallback((field: 'to' | 'from' = 'from'): SelectOption<string, TrustOptionData>[] => {
        // let accountTypes: TxTypeType[] = ['PC', 'FO'].includes(tr?.txType) ? ['O'] : [];
        let trustTypes: TrustTypes[] = ['G', 'M']; // By default, we show all client and matter trusts
        let showTrustAccounts = true; // Default: show them
        // let showBankAccounts = true;

        const tx = current;

        // For a "to" field:
        if (field === 'to') {
            // TODO: This section can never be called, and I'm not sure why it would be.
            //       We never need to know about the bank account.
            // If this is a payment from trust or a client, then we need bank accounts only
            // if (['PT', 'PC'].includes(props.txType) &&
            //     (tx.matterIds.length > 0) &&
            //     (tx.targetType === 'B') &&
            //     getBankAccounts(['O']).length > 0
            // ) {
            //     accountTypes = ['O'];
            //     showBankAccounts = true;
            //     showTrustAccounts = false;
            // }

            // If this is a [FR] (Funding of a Retainer), we are going to show either/both [G]eneral or [M]atter trusts
            // else {
            //     accountTypes = ['G', 'M'];
            //     showBankAccounts = false;
            showTrustAccounts = true;
            // }
        }

        // For a "from" field:
        else if (field === 'from') {
            // showBankAccounts = false;
            showTrustAccounts = true;
            // accountTypes = ['G', 'M'];

            // If there are matters entered, and the target type is [G] (Contact), and there are clients entered,
            // set trustTypes to [G] (Contact).
            if ((tx.matterIds.length > 1) &&
                (tx.targetType === 'G') &&
                (tx.clientIds.length > 1)
            ) {
                trustTypes = ['G'];
                showTrustAccounts = true;
            }

            // If no matters selected, show only general trusts
            else if (tx.matterIds.length === 0) {
                showTrustAccounts = true;
                trustTypes = ['G'];
            }
        }

        // const bankAccounts = showBankAccounts ? getBankAccounts(accountTypes) : [];
        return showTrustAccounts ? getTrustAccounts(trustTypes, field) : [];
    }, [current, getTrustAccounts]);

    /**
     * Get a list of bank accounts and trust accounts.  If client(s) or a matter are selected, we can show the trust for each of them.
     * @param {Transaction} tr - the current transaction
     * @return {{label: ReactElement | string, value: any: data: any}[]} - formatted objects with label, value and data
     */
    const getSourceAccounts = useCallback((tr: Transaction): SelectOption<string, TrustOptionData>[] => {
        // const txType = tr ? tr.txType : props.txType;
        // let accounts;

        // let accounts;
        // if (['FR', 'PC', 'PT'].includes(txType)) accounts = getContacts(tr);
        // else
        const accounts = getPaymentAccounts('from');

        if (tr) {
            return accounts.filter(at => at.data?.id === tr.source);
        }
        else {
            return accounts;
        }
    }, [getPaymentAccounts]);

    /**
     * Get a list of target accounts.
     *
     * If a transaction is passed, just return the object matching that transaction's target
     *
     * @param {Transaction} [tr]
     */
    const getTargetAccounts = useCallback((tr?: Transaction) => {
        const accounts = getPaymentAccounts('to');

        if (tr) {
            return accounts.filter(at => at.data?.id === tr.target);
        }
        else {
            return accounts;
        }
    }, [getPaymentAccounts]);

    // /**
    //  * List trust accounts for each client and matter, along with the operating account.
    //  *
    //  * TODO: What is going on here?
    //  *
    //  * @param {Transaction} tr
    //  * @param {'to'|'from'} [field]
    //  */
    // getTransferAccounts = (tr: Transaction, field: 'to' | 'from' = 'from') => {
    //     const bankAccounts = getBankAccounts([]);
    //     const trustAccounts = getTrustAccounts(['G', 'M'], field);
    //
    //     return [...trustAccounts, ...bankAccounts];
    // };

    /**
     * Get a list of payment methods.
     *
     * @param {Transaction} tr
     */
    const getPaymentMethods = useCallback((tr: Transaction) => {
        let methods: { label: ReactElement | string, value: string, paymentMethod?: PaymentMethod }[];

        if (/*current?.source &&*/ (['FR', 'PC'].includes(props.txType || tr?.txType)) && paymentApi.isConnected()) {
            const source = current.source;
            const pms = source ? paymentMethods.filter(pm => pm.contact?.id === source) : paymentMethods;

            methods = pms
                .map((paymentMethod) => {
                    const description = paymentMethod.description ||
                        (!!paymentMethod.accountHolderType
                         ? paymentMethod.accountType
                                // @ts-ignore: We have to deal with old LawPay data here
                         : util.snakeToText(paymentMethod.cardType || paymentMethod.custom?.methodName)
                        );

                    return {
                        label: <IconButton
                            type={paymentApi.getPaymentIcon(paymentMethod)}
                            label={<div className='transaction-card__payment-method'>
                                <span className='u-client'>{formatFML(paymentMethod.contact)}</span>
                                <span>{description}</span>
                                <span> ending in {paymentMethod.number?.slice(-4) ?? ''}</span>
                            </div>}
                        />,
                        // @ts-ignore: We have to deal with old LawPay data here
                        value: paymentMethod.paymentToken || paymentMethod.fingerprint,
                        paymentMethod
                    };
                });

            PAYMENT_PROCESSOR_METHODS.forEach(method => {
                methods.push({label: <PaymentMethodIcon method={method}/>, value: method});
            });

            /*
             * If this transaction has processorPaymentMeta with method data, we need to ensure
             * we can display it even if:
             * 1. The payment methods haven't loaded yet from the API
             * 2. The paymentMethod token doesn't match any fetched methods
             * 3. The transaction was done on a different payment system (Payrix vs LawPay)
             */
            if (current.processorPaymentMeta?.method) {
                const prefix = import.meta.env.MODE === 'development' ? 't1' : 'p1';
                const currentPaymentSystem = paymentApi.getPaymentProcessor();
                const oldPaymentSystem = current.processorPaymentMeta.id?.startsWith(`${prefix}_txn`)
                                         ? 'payrix'
                                         : 'lawpay';
                const isSystemMismatch = currentPaymentSystem !== oldPaymentSystem;

                /* Both Payrix and LawPay have similar structures:
                 * - type: 'card' or 'bank'
                 * - number: last4 or full masked number
                 * - cardType: card brand (Visa, Mastercard) or account type
                 * - accountType: bank account type
                 * - fingerprint: unique identifier
                 * LawPay may also have snake_case variants (card_type, account_type, account_number)
                 */
                const method = current.processorPaymentMeta.method as any;
                const type = method.type;
                const cardType = method.cardType || method.card_type;
                const accountType = method.accountType || method.account_type;
                const number = method.number || method.account_number;
                const fingerprint = method.fingerprint;

                // Determine if this is a bank or card transaction
                const isBank = type === 'bank' || !!method.account_number || !!method.routing;

                // Build description from available fields
                const typeLabel = isBank
                    ? upFirstLetter(accountType?.toLowerCase() || 'Bank Account')
                    : cardType || 'Card';
                const last4 = number?.slice(-4) || '****';
                const description = `${typeLabel} ending in ${last4}`;

                const metaEntry = {
                    label: <IconButton
                        type={isBank ? Icons.BANK : Icons.CREDIT_CARD}
                        label={<div className='transaction-card__payment-method'>
                            <span style={{marginRight: '5px'}}>{description}</span>
                            {isSystemMismatch && <span style={{
                                color: 'maroon',
                                fontWeight: 600
                            }}>from {upFirstLetter(oldPaymentSystem)}</span>}
                        </div>}
                    />,
                    value: current.paymentMethod || fingerprint || number,
                    paymentMethod: method
                };

                // Add at the beginning so it's found during filtering
                methods = [metaEntry, ...methods];
            }
        }
        else {
            methods = PAYMENT_METHODS.map(method => {
                return {label: <PaymentMethodIcon method={method}/>, value: method};
            });
        }

        if (tr) {
            return methods.filter(at => {
                // Simple, non paymentApi method
                if (Object.hasOwn(at, 'value') && at.value === tr.paymentMethod) return true;

                // Handles issue where the paymentMethod on the transaction doesn't match the value
                if (Object.hasOwn(at, 'paymentMethod')) {
                    const pm: any = at.paymentMethod;

                    // credit card
                    if (Object.hasOwn(pm, 'cardNumber') &&
                        `Card ending in ${pm.cardNumber}` === tr.paymentMethod) return true;

                    // bank account
                    if (Object.hasOwn(pm, 'accountNumber') &&
                        pm.bankAccount === tr.paymentMethod) return true;

                    // fingerprint
                    if (Object.hasOwn(pm, 'fingerprint') &&
                        pm.fingerprint === tr.paymentMethod) return true;

                    // number
                    if (Object.hasOwn(pm, 'number') &&
                        pm.number === (tr.processorPaymentMeta as any).number) return true;

                    // account_number
                    // if (Object.hasOwn(pm, 'account_number') &&
                    // 	pm.account_number === tr.processorPaymentMeta.account_number) return true;

                }

                return false;
            });
        }
        else return methods;
    }, [current.paymentMethod, current.processorPaymentMeta, current.source, paymentMethods, props.txType]);

    /**
     * Get a list of clients.
     *
     * Show the amount of funds available in the client trusts, if any
     *
     * @param {Transaction} tr
     * @return {SelectOption<string, Contact>[]} - formatted objects with label, value and data
     */
    const getClients = useCallback((tr: Transaction): SelectOption<string, Contact>[] => {
        // Get a unique list of all clients
        const matterClients = (CaseHelper.getClients(true) as uuid[])
            .filter(cid => tr ? ['PT', 'PC'].includes(tr.txType) // if a payment from trust or client
                                ? FinancialHelper.getTrustAmount('A', cid) > 0 // return true if there is money in either client or matter trusts
                                : true
                              : true);

        const leadClients = util.flatten(leads.map(l => l.clients));

        const getIcon = (c: Contact) => {
            if (!c) return null;
            if (matterClients.includes(c.id)) return Icons.CASE;
            else return Icons.LEAD;
        };

        const getColor = (c: Contact) => {
            if (matterClients.includes(c?.id)) return '#b3953c';
            else return 'green';
        };

        const clients = matterClients.concat(leadClients)
            .filter(util.distinctFilter)
            .filter(util.existsFilter)
            .map(cid => find<Contact>('contacts', cid))
            .sort(sortContacts)
            .filter(util.existsFilter)
            .map(c => {
                const name = formatLFM(c);
                return {
                    // c is non-null after the existsFilter type guard, so getIcon never hits
                    // its `if (!c) return null` branch — `!` asserts the always-present icon. (OWA-554)
                    label: <IconButton type={getIcon(c)!} color={getColor(c)} label={name}/>,
                    value: name,
                    data: c
                };
            });

        // Filter based on state, rather than the transaction
        if (tr) {
            // Only return entries for the clients in state
            return clients.filter(c => tr.clientIds.includes(c.data.id));
        }
        else return clients;
    }, [leads]);

    /**
     * Get a list of matters.  Only show matters where the selected clients are available.
     *
     * @param {Transaction} tr
     * @return {SelectOption<string, Matter>[]} - formatted objects with label, value and data
     */
    const getMatters = useCallback((tr: Transaction): SelectOption<string, Matter>[] => {
        // Find all matters related to these clients.
        const _matters = allMatters
            // Matter must include every client in clientIds
            .filter(m => current.clientIds.every(cid => m.clients.includes(cid)))
            .map(m => {
                return {label: m.name, value: m.name, data: m};
            });

        if (tr) {
            return _matters.filter(m => tr.matterIds.includes(m.data.id));
        }
        else return _matters;
    }, [allMatters, current.clientIds]);

    /**
     *
     * @param {SelectOption<string, Transaction>[]} values
     */
    const onClientsChange = useCallback((values: SelectOption<string, Transaction>[]) => {
        const clone = current.clone();
        // Select options always carry their data; `!` keeps clientIds a string[] and the
        // existing throw-on-missing behaviour. (OWA-554)
        clone.clientIds = values.map(v => v.data!.id);

        onChange(clone);
    }, [current, onChange]);

    /**
     * Called when asking how funds to multiple matters should be distributed.
     * For FR, (O)perating is not an option (use a PC/PT for that).
     *
     * If there are multiple clients, it can NOT go into a Client Trust (G)!
     *
     * @param {Transaction} [tr]
     * @return {DistributionTarget|DistributionTarget[]}
     */
    const getDistributeTo = useCallback((tr: Transaction): DistributionTarget[] => {
        const operating = {label: i18n.ls('transaction', 'operating'), value: 'B'};
        const matterTrusts = {label: i18n.ls('transaction', 'matterTrusts'), value: 'M'};
        const clientTrusts = {label: i18n.ls('transaction', 'generalTrust'), value: 'G'};

        if (tr) {
            return tr.targetType === 'B'
                   ? [operating]
                   : tr.targetType === 'M'
                     ? [matterTrusts]
                     : tr.targetType === 'G'
                       ? [clientTrusts]
                       : [];
        }
        else {
            let values: DistributionTarget[] = [];
            if (current.matterIds.length > 0) values.push(matterTrusts);
            if (current.clientIds.length < 2) values.push(clientTrusts);
            if (props.txType !== 'FR') values.push(operating);

            return values;
        }
    }, [current.clientIds.length, current.matterIds.length, props.txType]);

    /**
     * Get the payment distribution (if available)
     *
     * @param {Transaction} tr
     * @param {FormElement} element
     */
    const getPaymentDistribution = useCallback((tr: Transaction, element: FormElement) => {
        const _readOnly = readOnly || !!tr.invoiceId;

        if (tr.targetType === 'M' && tr.matterIds.length > 1) {
            // Determine the split.  Need an entry for each matter.
            const entries = tr.matterIds.map(mid => {
                // tr.matterIds are drawn from allMatters, so the find resolves; `!` preserves
                // the current throw-on-missing behaviour. (OWA-554)
                const filteredMatters = allMatters.find(m => m.id === mid)!;
                let entry = tr.paymentDistribution ? tr.paymentDistribution.find((entry: any) => entry.matterId === filteredMatters.id) : null;

                return [
                    <div key={`${filteredMatters.id}-amount`}
                         className='transaction-card__paymentDistribution-entry-amount'>
                        <div className="dollar-sign">$</div>
                        <input className='money'
                               value={entry ? entry.amount : 0}
                               type="number" step="0.01" min="-9999999999.99" max="9999999999.99"
                               required={true}
                               onChange={(e) => handlePaymentDistribution(e, filteredMatters)}
                               disabled={_readOnly}
                        />
                    </div>,
                    <div key={`${filteredMatters.id}-name`}
                         className='transaction-card__paymentDistribution-entry-matter'>
                        {filteredMatters.name}
                    </div>
                ];
            }).flat();

            return element.renderElement('paymentDistribution', i18n.ls('transaction', 'distribution'), (<>{entries}</>));
        }
        else return null;
    }, [allMatters, handlePaymentDistribution, readOnly]);

    /**
     * User has selected the distribution method, which is now the targetType.
     *
     * We can be smart and figure out the client trust.
     *
     * @param {string} value - the targetType ('G', 'M', 'B')
     */
    const onDistributeTo = useCallback((value: 'G' | 'M' | 'B') => {
        const clone = current.clone();
        clone.targetType = value;

        // Default the client trust to the first client listed.
        if (value === 'G') clone.target = clone.clientIds[0];
        else if (value === 'B') {
            const accounts = bankAccounts.filter(ba => ba.accountType === 'O');
            if (accounts.length === 1) clone.target = accounts[0].bankAccountId;
        }


        onChange(clone);
    }, [bankAccounts, current, onChange]);

    /**
     * Right now this only selects one value, because distributions is not capable of handling
     * multiple matters, each with its own source... when that is ready, switch over to the version
     * below.
     *
     * @param {SelectOption<string, Matter>[]} values
     */
    const onMattersChange = useCallback((values: SelectOption<string, Matter>) => {
        const clone = current.clone();

        if (values) clone.matterIds = [values.data!.id];
        else clone.matterIds = [];

        // If we are doing a PT or PC, the case is the target
        if (clone.txType === 'PT') {
            if (values) {
                clone.targetType = 'M';
                clone.target = (values as SelectOption<string, Matter>).data!.id;
                clone.paymentMethod = 'trust';
            }
        }

        onChange(clone);
    }, [current, onChange]);

    /**
     * A payment has been processed.  Do NOT let them cancel out!
     *
     * @param {PayrixPaymentMeta} meta - the payment meta data
     */
    const onPaymentActionProcess = useCallback((meta: PayrixPaymentMeta) => {
        try {
            const clone = current.clone();
            clone.refusedDate = null;
            clone.voidedDate = null;
            clone.processorPaymentMeta = meta;

            setHasPaymentBeenTaken(true, () => {
                onChange(clone);
            });
        }
        catch (err: any) {
            console.error('PaymentCard.onPaymentActionProcess:', err);
        }
    }, [current, onChange, setHasPaymentBeenTaken]);

    /**
     * The payment has been marked as refused by the processor.
     *
     * @param {PayrixPaymentMeta} meta - the payment meta data
     */
    const onPaymentRefused = useCallback((meta: PayrixPaymentMeta) => {
        try {
            const clone = current.clone();
            clone.refusedDate = new Date();
            if (meta) clone.processorPaymentMeta = meta;

            onChange(clone);
        }
        catch (err: any) {
            console.error('PaymentCard.onPaymentRefused:', err);
        }
    }, [current, onChange]);

    /**
     * The payment has been marked as voided by the processor.
     *
     * @param {PayrixPaymentMeta} meta - the payment meta data
     */
    const onPaymentVoided = useCallback((meta: PayrixPaymentMeta) => {
        try {
            const clone = current.clone();
            clone.voidedDate = voidedDateFromMeta(meta); // OWA-187 / OWA-570
            if (meta) clone.processorPaymentMeta = meta;

            onChange(clone);
        }
        catch (err: any) {
            console.error('PaymentCard.onPaymentVoided:', err);
        }
    }, [current, onChange]);

    /**
     * The payment has been refunded by the processor.
     *
     * @param {PayrixPaymentMeta} meta - the payment meta data
     */
    const onPaymentRefunded = useCallback((meta: PayrixPaymentMeta) => {
        try {
            const clone = current.clone();
            // fetchTxn routes BOTH historical voids and refunds here, so a
            // months-old refund must keep its real date. (OWA-187 / OWA-570)
            clone.voidedDate = voidedDateFromMeta(meta);
            if (meta) clone.processorPaymentMeta = meta;

            onChange(clone);
        }
        catch (err: any) {
            console.error('PaymentCard.onPaymentRefunded:', err);
        }
    }, [current, onChange]);

    /**
     * The payment has been restored (a refund has been voided).
     *
     * @param {PayrixPaymentMeta} meta - the payment meta data
     */
    const onPaymentRestored = useCallback((meta: PayrixPaymentMeta) => {
        try {
            const clone = current.clone();
            clone.voidedDate = null;
            clone.refusedDate = null;
            if (meta) clone.processorPaymentMeta = meta;

            onChange(clone);
        }
        catch (err: any) {
            console.error('PaymentCard.onPaymentRestored:', err);
        }
    }, [current, onChange]);

    /**
     * Called when a payment is processed or changed in some way
     *
     * @param {ProcessorPaymentMeta} meta
     * @async
     */
    const onPaymentActionChange = useCallback(async (meta: ProcessorPaymentMeta) => {
        try {
            if (!deepEqual(current.processorPaymentMeta, meta)) {
                const clone = current.clone();
                clone.processorPaymentMeta = meta;

                if (meta.status) {
                    // TODO: Added to get other information. Needed though?
                    // const txn = await paymentApi.getTransaction(meta.id);

                    const {flags} = paymentApi.getCurrentStatus(clone);

                    // If we just kicked off a charge, put in today's date. OWA-843: only a
                    // charge. A Payrix void keeps status Approved, so it classifies as
                    // VOID plus INCOMPLETE and would otherwise restamp this date.
                    if (flags.includes('CHARGE') && flags.includes('INCOMPLETE')) clone.processFundsDate = new Date();

                    // If we just got back a completion event, use the completion date
                    if (util.arrayIncludesAllTerms(['COMPLETE', 'CHARGE'], flags)) {
                        const dt = paymentApi.getCompletionDate(clone);
                        // OWA-843: an absent completion date is not an instruction to
                        // clear a recorded one. Payrix meta carries no completion_date
                        // at all, so assigning null here erased the date on refresh.
                        if (dt) clone.fundsAvailableDate = new Date(dt);
                    }

                    // If there is an authorization code, use it as the reference number
                    // @ts-expect-error - authorization_code is from Lawpay
                    if (meta.authorization || meta.authorization_code) clone.referenceNumber = meta.authorization || meta.authorization_code;

                    // Handle voids and refunds. This handler also runs on the
                    // refresh path (PaymentActionBox.fetchTxn → props.onChange),
                    // AFTER onPaymentRefunded, so it must use the processor's
                    // actual void/refund date too — otherwise it re-clobbers a
                    // historical date with "now". (OWA-187 / OWA-570)
                    if (flags.includes('VOID') || util.arrayIncludesAllTerms(flags, ['REFUND', 'INCOMPLETE'])) {
                        clone.voidedDate = voidedDateFromMeta(meta);
                    }

                    // Just save the new version, then let the parent know it changed
                    dispatch(TransactionActions.saveTransactionQueue(clone));
                    onChange(clone, true);
                }
            }
        }
        catch (err: any) {
            console.error('PaymentCard.onPaymentActionChange:', err);
        }
    }, [current, dispatch, onChange]);

    /**
     *
     * @param {string} value - This is the token for the payment method, not the payment method itself!
     */
    const onPaymentMethodChange = useCallback((value: SelectOption<string, string>) => {
        const clone = current.clone();
        clone.paymentMethod = value.value;

        onChange(clone);
    }, [current, onChange]);

    const onSourceChange = useCallback((value: SelectOption<string, TrustOptionData>) => {
        const clone = current.clone();
        clone.source = value.data!.id;
        clone.sourceType = value.data!.type;

        onChange(clone);
    }, [current, onChange]);

    const onPayerChange = useCallback((contactId: uuid) => {
        const requiresUpdate = contactId !== current.id;

        const clone = current.clone();
        clone.source = contactId;
        clone.sourceType = 'C';
        clone.addedOn = newHRTimestamp();
        clone.addedBy = getCurrentUserId();

        // Make sure the paymentMethod is still valid (if using a payment processor only).
        // `.some(===)` is behaviour-identical to `.map().includes()` here and avoids the
        // string|null vs string|undefined mismatch on the token/paymentMethod types. (OWA-554)
        const usingPM = paymentMethods.some(pm => pm.paymentToken === clone.paymentMethod);
        if (usingPM) {
            // Set the payment method to null
            clone.paymentMethod = '';
        }

        onChange(clone);

        if (requiresUpdate) updateProcessorInfo();
    }, [current, onChange, paymentMethods, updateProcessorInfo]);

    /**
     * Change in the target of the funds.  Need to update txType here!
     *
     * @param {{label: string, value: string, data: {id: uuid, type: string, name: string, balance: number}}} value
     */
    const onTargetChange = useCallback((value: SelectOption<string, TrustOptionData>) => {
        const clone = current.clone();
        const targetType = value.data!.type;

        clone.target = value.data!.id;
        clone.targetType = targetType;

        // If we are not in a case (general transactions), and we don't have a matterId yet, use this one if it is a matter
        if (targetType === 'M' && clone.matterIds.length === 0) {
            clone.matterIds = [value.data!.id];
            if (isMounted()) setIsMatterSet(true);
        }
        else if (targetType === 'G' && clone.matterIds.length > 0 && isMatterSet) {
            clone.matterIds = [];
            if (isMounted()) setIsMatterSet(false);
        }

        onChange(clone);
    }, [current, isMatterSet, isMounted, onChange])

    /**
     * @param {Date} dt
     */
    const onTransactionDateChanged = useCallback((dt: Date) => {
        const clone = current.clone();
        clone.txDate = dt;

        onChange(clone);
    }, [current, onChange]);

    /**
     * @param {Date} dt
     */
    const onProcessFundsDate = useCallback((dt: Date) => {
        const clone = current.clone();
        clone.processFundsDate = dt;

        onChange(clone);
    }, [current, onChange]);

    /**
     * @param {Date} dt
     */
    const onFundsAvailableDate = useCallback((dt: Date) => {
        const clone = current.clone();
        clone.fundsAvailableDate = dt;

        onChange(clone);
    }, [current, onChange]);

    /**
     * @param {Date} dt
     */
    const onRefusedDate = useCallback((dt: Date) => {
        const clone = current.clone();
        clone.refusedDate = dt;

        onChange(clone);
    }, [current, onChange]);

    /**
     * Refused checkbox clicked.
     */
    const onRefusedCheck: CheckboxClickEvent = useCallback((e, data) => {
        const clone = current.clone();
        clone.refusedDate = clone.refusedDate ? null : new Date();

        onChange(clone);
    }, [current, onChange]);

    /**
     * @param {Date} dt
     */
    const onVoidedDate = (dt: Date) => {
        const clone = current.clone();
        clone.voidedDate = dt;

        onChange(clone);
    };

    /**
     * Voided checkbox clicked.
     */
    const onVoidedCheck = useCallback(() => {
        const clone = current.clone();
        clone.voidedDate = clone.voidedDate ? null : new Date();

        onChange(clone);
    }, [current, onChange]);

    const handleCancel = useCallback(() => {
        doResetRef.current = true;
        onCancel?.();
    }, [onCancel]);

    const handleOk = useCallback(() => {
        doResetRef.current = true;
        onOk?.(current);
    }, [current, onOk]);

    /**
     * Handle normal field changes.
     *
     * @param e
     * @param {string} _field
     * @param {string} text
     */
    const handleDescription = useCallback((e: ChangeEvent<HTMLInputElement>, _field: string, text: string) => {
        e.preventDefault();

        const clone = current.clone();
        clone.notes = (text || e.target.value) ?? '';

        onChange(clone, false);
    }, [current, onChange]);

    /**
     * @param {FormElement} element
     * @return {ReactElement}
     */
    const renderClientName = useCallback((element: FormElement): ReactElement => {
        let label = i18n.c('client', true);
        let value: string;

        if (client) {
            value = formatLFM(client, true);
        }
        else if (lead || matter || current.entityType === 'lead') {
            const obj = lead || matter || leads.get(current.entityId);

            label = i18n.c('clients', true);

            // Reached only when lead/matter is set or entityType === 'lead'; `!` preserves
            // the current throw-if-the-lead-is-missing behaviour. (OWA-554)
            value = obj!.clients
                .map(cid => formatFMLFromId(cid, false))
                .join(', ');
        }
        else {
            label = i18n.c('clients', true);

            value = current
                .clientIds
                .map(cid => formatFMLFromId(cid, false))
                .join(', ');
        }

        return element.renderElement('clientName', label, <div>{value}</div>, true);
    }, [client, current.clientIds, current.entityId, current.entityType, lead, leads, matter]);

    /**
     * Render the matter name, if a matter is specified.
     *
     * @param {FormElement} element
     * @returns {ReactElement}
     */
    const renderMatterName = useCallback((element: FormElement): ReactElement => {
        const label = lead || current.entityType === 'lead'
                      ? i18n.c('lead')
                      : i18n.c('case', true);

        const name = lead
                     ? lead.name
                     : matter
                       ? matter.name
                       : current.entityType === 'lead'
                         ? leads.get(current.entityId)!.name
                         : i18n.c('unknown', true);

        return element.renderElement('matterName', label, <div>{name}</div>, true);
    }, [current.entityId, current.entityType, lead, leads, matter]);

    /**
     * Render the matter name, if a matter is specified.
     *
     * @param {FormElement} element
     * @returns {ReactElement}
     */
    const renderMatterNames = useCallback((element: FormElement): ReactElement => {
        const matterNames = current.matterIds.map(mid => {
            let matterOrLead: Matter | Lead | undefined = CaseHelper.getMatterFromId(mid);
            if (!matterOrLead) matterOrLead = LeadHelper.getLeadFromId(mid);
            if (matterOrLead) return matterOrLead.name;
            else return '???';
        }).join(', ');
        return element.renderElement('matterName', 'Case',
            <div>{matterNames}</div>, true);
    }, [current.matterIds]);

    /**
     * See if the sub-transactions add up.
     *
     * @param {Transaction} tr
     * @returns {boolean}
     */
    const addsUp = useCallback((tr: Transaction): boolean => {
        if (tr.matterIds?.length > 1 && tr.targetType === 'M') {
            // Ensure each matter has an entry, and that they all add up to Amount
            if (!tr.paymentDistribution) return false;
            else if (tr.paymentDistribution.length !== tr.matterIds.length) return false;
            else return tr.paymentDistribution.reduce((acc, cur) => acc + Number.parseFloat(cur.amount), 0) === tr.amount;
        }
        else return true;
    }, []);

    /**
     * Helper function for brevity.
     * @param {string} key
     * @param {object} [params]
     * @returns {ReactElement|string}
     */
    const l = useCallback((key: string, params?: any): string => i18n.ls('transaction', key, params), []);

    const element = props.element;
    const txType = props.txType;
    const onlinePayments = ['FR', 'PC'].includes(txType) && paymentApi.isConnected();
    // `.some(===)` is behaviour-identical to `.map().includes()` and sidesteps the
    // string|null vs string|undefined token/paymentMethod mismatch. (OWA-554)
    const usingPM = paymentApi.usesPayments() && paymentMethods.some(p => p.paymentToken === current.paymentMethod);

    let pm: PaymentMethod | undefined;
    let flags: string[] = [];
    let txReadOnly = false;

    if (usingPM) {
        pm = paymentMethods.find(p => p.paymentToken === current.paymentMethod);

        // Determine readOnly for a transaction that has been processed.
        // 1. If a transaction is in progress or has settled, it should be read only
        // 2. If a transaction has been voided or fully refunded, it can be changed.
        flags = paymentApi.getCurrentStatus(current)?.flags;

        txReadOnly = flags.length > 0 && flags.includes('COMPLETE') && !Auth.isAdmin();
    }

    const transactionProcessed = !!current.refusedDate ||
        !!current.voidedDate ||
        (onlinePayments // if using online payments
         ? usingPM // and we have selected an online payment method
           ? flags.length > 0
           : true
         : true);

    const trustOnly = txType === 'PT';

    // Should we show the void/refund checkbox?
    const showVoided = usingPM ? flags.includes('VOID') || flags.includes('REFUND') : !trustOnly;

    const _readOnly = txReadOnly || readOnly || (current.invoiceId && FinancialHelper.getInvoiceById(current.invoiceId));

    /**
     * Need to ensure that the amounts add up if there is a split of some kind.  Only needed for payments
     */
    const doesItAddUp = addsUp(current);

    // See if there is sufficient funds to make the transaction. Applies to PC/PT payments only
    // let sufficientFunds = true;
    // if (txType !== 'FR' && txType !== 'PC' && tr.source && tr.amount) {
    //     // Check the source to see if there are enough funds for the amount requested
    //     const notThisAmount = props.transactions.find(tx => tx.id === tr.id && tx.status !== 'D')?.amount ?? 0;
    //     const trustAmount = FinancialHelper.getTrustAmount(tr.sourceType, tr.source);
    //     sufficientFunds = trustAmount + notThisAmount >= tr.amount;
    // }

    // "Please click on Process Payment" only applies when we have a Payrix-style saved-card
    // payment method selected but the charge has NOT yet been processed for this transaction.
    // A reopened transaction that already has processorPaymentMeta has already been charged.
    const needsToProcessPayment = onlinePayments
                                  && usingPM
                                  && !!current.paymentMethod
                                  && !current.processorPaymentMeta
                                  && !hasPaymentBeenTaken;

    // We can now determine if we are clear to save.
    // Note: the trailing ternary MUST be parenthesised — otherwise `||` binds tighter than
    // `?:` and the entire `||` chain collapses into the ternary's condition.
    const disabled =
        !props.hasChanged ||
        !current.txDate ||
        current.clientIds.length === 0 ||
        ['FR', 'PC'].includes(current.txType) && !current.source ||
        current.txType === 'PT' && !current.source ||
        current.amount <= 0 ||
        !doesItAddUp ||
        (!current.target && current.targetType !== 'B') && !lead ||
        current.txType !== 'PT' && !current.paymentMethod ||
        needsToProcessPayment ||
        !current.isValid();

    const disabledMessage = failureLadder([
        [!current.txDate, l('need', {v: 'date'})],
        [current.clientIds.length === 0, l('need', {v: 'client'})],
        // [tr.matterIds.length === 0, l('need', {v: 'case'})],
        [['FR', 'PC'].includes(current.txType) && !current.source, l('need', {v: 'payer'})],
        [current.txType === 'PT' && !current.source, l('need', {v: 'trust'})],
        [current.amount <= 0, l('need', {v: 'amount', an: true})],
        [!doesItAddUp, l('doesNotAddUp')],
        [(!current.target && current.targetType !== 'B') && !lead, l('need', {v: 'target account'})],
        [current.txType !== 'PT' && !current.paymentMethod, l('need', {v: 'payment method'})],
        [needsToProcessPayment, 'Please click on Process Payment'],
        [!current.isValid(), current.validationError ?? '']
    ]);

    // Whether to show the target account is complicated.
    let showTargetAccount = true;

    /* Can we guess the target?
     * 1 Client, 2 Matters, targetType C or M: Yes
     * 1 Client, 2 Matters, targetType O, 1 Operating Account: Yes
     * 2 Clients, 2 Matters, targetType M: Yes
     */
    // If we can be certain that there is only one client trust, and we have chosen a client trust, we can hide it
    if (current.matterIds.length > 1) {
        if (current.clientIds.length === 1 && (current.targetType === 'G' || current.targetType === 'M')) showTargetAccount = false;
        if (current.clientIds.length > 1 && current.targetType === 'M') showTargetAccount = false;
    }

    // Payments only -- determine the payment distribution
    let paymentDistribution = getPaymentDistribution(current, element);

    // Which processor's accountId is this related to (only if using payments)
    let accountId: string | undefined;
    if (usingPM) {
        // usingPM implies current.paymentMethod matched a stored token, so the find above
        // resolved — `!` asserts the payment method we just located. (OWA-554)
        const type = pm!.type;

        if (current.txType === 'FR') { // Use trust account
            if (paymentApi.isLawpay()) {
                if (type === 'bank') {
                    accountId = currentBankAccounts.find(ba => ba.trustAccount && ba.accountType === 'AchAccount')?.theirId;
                }
                else accountId = currentBankAccounts.find(ba => ba.trustAccount && ba.accountType !== 'AchAccount')?.theirId;
            }
            else accountId = currentBankAccounts.find(ba => ba.trustAccount)?.theirId;
        }
        else { // Use operating account
            if (paymentApi.isLawpay()) {
                if (type === 'bank') accountId = currentBankAccounts.find(ba => !ba.trustAccount && ba.accountType === 'AchAccount')?.theirId;
                else accountId = currentBankAccounts.find(ba => !ba.trustAccount && ba.accountType === 'MerchantAccount')?.theirId;
            }
            else accountId = currentBankAccounts.find(ba => !ba.trustAccount)?.theirId;
        }
    }

    // Read-only version
    if (_readOnly) return (
        <>
            {/* Button bar */}
            {(!!onOk && !!onCancel) && !props.infoOnly &&
				<OkCancel label={l('title')}
				          disabled={disabled}
				          useDisabledMessage={true}
				          disabledMessage={disabledMessage}
				          onCancel={handleCancel}
				          onSubmit={handleOk}
				          onTop={true}
				          cancelDisabled={false}
				/>
            }

            {/* Transaction Date */}
            {element.renderElement('txDate',
                <HelpMessage module='transaction' field='date'/>,
                DateUtil.formatDateTime(current.txDate, true))}

            {/* Client(s) */}
            {renderClientName(element)}

            {/* Matter(s) */}

            {/* Single matter specified in props - this is being edited in a CaseCard */}
            {(lead || current.entityType === 'lead' || (matter && current.matterIds.length === 1)) && renderMatterName(element)}

            {/* No matter specified, but at least one client specified - this is being edited in a ContactCard */}
            {!lead && !matter && (current.clientIds.length > 0 || current.matterIds.length > 1) && (current.entityType !== 'lead') &&
                renderMatterNames(element)
            }

            {/* If more than one matter, ask if it will go into case trusts, a client trust, or an operating account */}
            {current.matterIds.length > 1 && element.renderElement(
                'targetType',
                l('depositTo'),
                getDistributeTo(current)[0].label)
            }

            {/* Source */}
            {['FR', 'PC'].includes(txType) && element.renderElement(
                'source',
                <HelpMessage module='transaction' field={isPortalUser() ? 'payerPortal' : 'payer'}/>,
                getContacts(current)
                    .map((o, i) => <div
                        key={i}>{o.label}</div>))}

            {['FO', 'PT'].includes(txType) && element.renderElement('source',
                <HelpMessage module='transaction' field='trust'/>,
                getSourceAccounts(current)
                    .map((o) => o.label))}

            {/* Amount */}
            {element.renderElement('amount',
                l('amount'),
                util.formatCurrency(current.amount))}

            {/* If a split, and we are putting into case trusts, ask for breakdown */}
            {current.matterIds.length > 1 && current.targetType === 'M' && paymentDistribution}

            {/* Target - for payment to trust. All other payments are to the operating account */}
            {txType === 'FR' && showTargetAccount && element.renderElement('target',
                <HelpMessage module='transaction' field='toAccount'/>,
                getTargetAccounts(current).map((o, i) =>
                    <div
                        key={i}>{o.label}</div>))
            }

            {/* Payment method */}
            {!trustOnly && element.renderElement('paymentMethod',
                <HelpMessage module='transaction' field='paymentMethod'/>,
                isFetching
                ? 'Loading Payment Methods...'
                : getPaymentMethods(current).map((o, i) => <div key={i}>{o.label}</div>))
            }

            {/* Invoice */}
            {current.invoiceId && FinancialHelper.getInvoiceById(current.invoiceId) && element.renderElement('invoice',
                <HelpMessage module='transaction' field='invoice'/>,
                <div>{FinancialHelper.getInvoiceById(current.invoiceId)?.invoiceNumber}</div>)
            }

            {/* An optional reference number. Don't show if we are using a payment processor and the transaction
				 has not been run yet!
				 */}
            {!trustOnly && !!current.referenceNumber && element.renderElement('referenceNumber',
                <HelpMessage module='transaction' field='referenceNumber'/>,
                current.referenceNumber)}

            {/* Process Funds Date */}
            {!trustOnly && !!current.processFundsDate && element.renderElement('processFundsDate',
                <HelpMessage module='transaction' field='processOn'/>,
                DateUtil.formatDate(current.processFundsDate, true))
            }

            {/* Funds Available Date */}
            {!trustOnly && !!current.fundsAvailableDate && element.renderElement('fundsAvailableDate',
                <HelpMessage module='transaction' field='fundsAvailable'/>,
                DateUtil.formatDate(current.fundsAvailableDate, true))
            }

            {/* Refused Date */}
            {current.refusedDate && !trustOnly && element.renderElement('refusedDate',
                <HelpMessage module='transaction' field='refusedOn'/>,
                DateUtil.formatDate(current.refusedDate, true))
            }

            {/* Voided Date */}
            {current.voidedDate && element.renderElement('voidedDate',
                <HelpMessage module='transaction' field='voidedOn'/>,
                DateUtil.formatDate(current.voidedDate))
            }

            {/* Notes */}
            {isPortalUser() ? element.renderElement('notes',
                                <HelpMessage module='transaction' field='notes'/>,
                                <div>{current.notes}</div>
                            )
                            : element.renderMultilineTextOnly('notes',
                    <HelpMessage module='transaction' field='notes'/>,
                    false, {disabled: props.infoOnly})
            }

            {/* Processor */}
            {usingPM && accountId &&
				<PaymentActionBox transaction={current}
				                  paymentMethod={pm}
				                  accountId={accountId}
				                  lead={lead}
				                  isConsultPayment={props.isConsultPayment}
				                  matter={matter}
				                  onChange={onPaymentActionChange}
				                  onProcess={onPaymentActionProcess}
				                  onRefunded={onPaymentRefunded}
				                  onRefused={onPaymentRefused}
				                  onRestored={onPaymentRestored}
				                  onVoided={onPaymentVoided}
				                  ref={paymentActionBoxRef}
				/>}

        </>
    );

    else return (
        <>
            {/* Button bar */}
            {hasPaymentBeenTaken && onOk && !props.infoOnly &&
				<OkCancel label={l('title')}
				          disabled={false}
				          cancelDisabled={true}
				          onCancel={util.noop}
				          onSubmit={handleOk}
				          onTop={true}
				/>
            }
            {((onlinePayments ? !hasPaymentBeenTaken : true) &&
                    onOk &&
                    onCancel
                ) && !props.infoOnly &&
				<OkCancel label={l('title')}
				          disabled={disabled}
				          useDisabledMessage={true}
				          disabledMessage={disabledMessage}
				          onCancel={handleCancel}
				          onSubmit={handleOk}
				          onTop={true}
                    // likesEntityType='transaction'
                    // likesEntityId={tr.id}
				/>
            }

            {/* Transaction Date */}
            {isPortalUser()
             ? element.renderElement('txDate',
                    <HelpMessage module='transaction' field='date'/>,
                    DateUtil.formatDateTime(current.txDate, true))
             : element.renderDateTime('txDate',
                    <HelpMessage module='transaction' field='date'/>,
                    onTransactionDateChanged, false, true)
            }

            {/* Client(s)
				 *
				 * -- Always show! --
				 *
				 * Allow multiple clients ONLY if they both exist within the same case.  That means we will need to
				 * filter available contacts based on clients in cases.
				 *
				 * If we are passed a client or matter, that means we are within the Contacts or Cases page,
				 * and we need to be restricted to those entities.
				 */}
            {(client || lead || matter)
             ? renderClientName(element) // Just render the client name, no choices
             : element.renderSelect('clients',
                    <HelpMessage field='clients' module='transaction'/>,
                    true, false, getClients, onClientsChange, true, {disablePortaling: props.portaled})
            }

            {/* Matter(s) */}

            {/* Single matter specified in props - this is being edited in a CaseCard */}
            {(lead || matter) && current.matterIds.length === 1 && renderMatterName(element)}

            {/* No matter specified, but at least one client specified - this is being edited in a ContactCard */}
            {!lead && !matter && (current.entityType !== 'lead') && (current.clientIds.length > 0 || current.matterIds.length > 1) &&
                element.renderSelect('matterIds',
                    <HelpMessage module='transaction' field='matterIds'/>,
                    current.txType !== 'PT', false, getMatters, onMattersChange, true,
                    {
                        placeholder: 'Leave empty for payment to general trust',
                        disablePortaling: props.portaled
                    })
            }

            {current.entityType === 'lead' &&
                element.renderElement('matterIds',
                    <HelpMessage module='transaction' field='lead'/>,
                    <IconButton type={Icons.LEAD}
                                label={LeadHelper.getLeadFromId(current.entityId)?.name}/>
                )}

            {/* If more than one matter, ask if it will go into case trusts, a client trust, or an operating account */}
            {current.matterIds.length > 1 && element.renderSelect('targetType',
                <HelpMessage module='transaction' field='depositTo'/>,
                false, false, getDistributeTo, onDistributeTo, false, {disablePortaling: props.portaled})}

            {/* Source */}
            {['FR', 'PC'].includes(txType) && element.renderElement(
                'source',
                <HelpMessage module='transaction' field={isPortalUser() ? 'payerPortal' : 'payer'}/>,
                <ContactSelect current={current.source ?? ''}
                               isMulti={false}
                               name='PaymentCardPayer'
                               parentId={current.id}
                               onChange={onPayerChange}
                               ref={payerRef}
                               autoSave={true}
                               disablePortaling={props.portaled}
                               options={contactOptions}
                               allowCreate={false}
                />, true)
            }

            {['FO', 'PT'].includes(txType) && element.renderSelect('source',
                <HelpMessage module='transaction' field='trust'/>,
                false, false, getSourceAccounts, onSourceChange, true,
                {disablePortaling: props.portaled})}

            {/* Amount — non-negative; refunds and discounts use their own txTypes (see Help Me Decide). */}
            {element.renderInput('amount', l('amount'), 'money', true, undefined, 0)}

            {/* If a split, and we are putting into case trusts, ask for breakdown */}
            {current.matterIds.length > 1 && current.targetType === 'M' && paymentDistribution}

            {/* Target - for payment to trust. All other payments are to the operating account */}
            {txType === 'FR' && showTargetAccount && element.renderSelect('target',
                <HelpMessage field='toAccount' module='transaction'/>,
                false, false, getTargetAccounts, onTargetChange, true,
                {disablePortaling: true})
            }

            {/* Payment method */}
            {!trustOnly && element.renderSelect('paymentMethod',
                        <HelpMessage module='transaction' field='paymentMethod'/>,
                        false,
                        false,
                        getPaymentMethods,
                        onPaymentMethodChange,
                        true,  // usesExtraFields=true because Payrix methods have ReactElement labels
                        {disablePortaling: true, placeholder: isFetching ? 'Loading payment methods' : ''}
            )}

            {/* Invoices */}
            {current.invoiceId && FinancialHelper.getInvoiceById(current.invoiceId) && element.renderElement('invoices',
                <HelpMessage module='transaction' field='invoice'/>,
                <div/>)}

            {/* An optional reference number */}
            {!isPortalUser() && transactionProcessed && !trustOnly && element.renderInput('referenceNumber',
                <HelpMessage module='transaction' field='referenceNumber'/>,
                'text', false)}

            {/* Process Funds Date */}
            {!isPortalUser() && !usingPM && !trustOnly && element.renderDate(
                'processFundsDate',
                <HelpMessage module='transaction' field='processOn'/>,
                onProcessFundsDate,
                false,
            )}

            {/* Funds Available Date */}
            {!isPortalUser() && !trustOnly && element.renderDate(
                'fundsAvailableDate',
                <HelpMessage field='fundsAvailable' module='transaction'/>,
                onFundsAvailableDate,
                false
            )}

            {/* Refused Date. OWA-953: only for a payment recorded with one of the manual methods and
                no processor meta, i.e. taken through a processor we do not integrate. Payrix and LawPay
                refusals come from the processor. A saved processor method is an opaque token, so it
                never matches the list, whether or not the saved methods have loaded. */}
            {!isPortalUser() && transactionProcessed && !current.voidedDate && !trustOnly &&
             PAYMENT_METHODS.includes(current.paymentMethod ?? '') && !current.processorPaymentMeta?.id &&
				<DateWithCheckbox current={current}
				                  disabled={false}
				                  onCheckboxClicked={onRefusedCheck}
				                  onDateChanged={onRefusedDate}
				                  uiParent={'transaction-card'}
				                  paramName={'refusedDate'}
				                  uiLabel={<HelpMessage field='refusedOn' module='transaction'/>}
				/>}

            {/* Voided Date */}
            {!isPortalUser() && showVoided &&
                <DateWithCheckbox current={current}
                                  disabled={false}
                                  onCheckboxClicked={onVoidedCheck}
                                  onDateChanged={onVoidedDate}
                                  uiParent={'transaction-card'}
                                  paramName={'voidedDate'}
                                  uiLabel={<HelpMessage field='voidedOn' module='transaction'/>}
                />
            }

            {/* Notes */}
            {!isPortalUser() && element.renderMultilineTextOnly('notes',
                <HelpMessage module='transaction' field='notes'/>,
                false, {onBlur: handleDescription})}

            {/* Processor */}
            {usingPM && current.amount > 0 && accountId &&
				<PaymentActionBox transaction={current}
				                  paymentMethod={pm}
				                  accountId={accountId}
				                  lead={lead}
				                  isConsultPayment={props.isConsultPayment}
				                  matter={matter}
				                  onChange={onPaymentActionChange}
				                  onProcess={onPaymentActionProcess}
				                  onRefunded={onPaymentRefunded}
				                  onRefused={onPaymentRefused}
				                  onRestored={onPaymentRestored}
				                  onVoided={onPaymentVoided}
				/>
            }
        </>
    );
}

export type PaymentCardProps = TransactionChildCardProps & Pick<RootState, 'bankAccounts' | 'contacts' | 'leads' | 'matters' | 'misc'>;

export default PaymentCard;
